/**
 * Experiment registry — POLICY-AS-DATA for the holdout/causal-measurement rail
 * (WS-F). Every offer/surface lever that wants a control arm declares itself
 * here; the runtime (`lib/experiment.ts`) assigns deterministically from this
 * config, so turning an experiment on/off or reweighting arms is a config edit,
 * never a component edit (rule 40 policy-as-data discipline).
 *
 * Lifecycle contract:
 *   - `status: "off"`    → assignment + exposure + payment attribution are inert.
 *                          Define the experiment BEFORE wiring variants so the
 *                          id/arms are reviewed once and stay stable.
 *   - `status: "active"` → `getExperimentArm()` returns an arm for clients
 *                          admitted by the optional pilot allocation,
 *                          `trackExperimentExposure()` emits `experiment_exposed`
 *                          (once per session per experiment), and every
 *                          `createPaymentOrder` sends "expId:arm,..." to the
 *                          private server-owned `payment.order_create` audit.
 *                          Captured money joins by order_id; provider notes
 *                          remain opaque and carry no experiment/user identity.
 *   - Never rename a shipped experiment id or arm id mid-flight — the id is the
 *     hash seed, so a rename reshuffles every user's arm and invalidates the
 *     readout. Retire (status "off") and start a new id instead.
 *
 * Readout: `uv run python execution/experiment_readout.py --experiment <id> \
 *   --start <YYYY-MM-DD> --end <YYYY-MM-DD>` (free DuckDB lake, no BigQuery).
 *
 * Relationship to `lib/experiments.ts` (ADR-0034): that registry owns the live
 * 2-arm reading_id-scoped loop (BQ `v_experiment_readout` + the A/A guardrail
 * `exposure_pipeline_v1`). New multi-arm / browser-scoped experiments belong
 * HERE; both rails emit the one `experiment_exposed` event (see
 * `lib/experiment.ts` header for the full reconciliation + convergence path).
 */

import { EXPERIMENT_ASSIGNMENT_POLICIES } from "./experiment-assignment-policy.generated";

export interface ExperimentArm {
  readonly id: string;
  /** Relative weight (any positive number; normalized across the arms array). */
  readonly weight: number;
}

export interface ExperimentPilotPolicy {
  /** Deterministic share of otherwise eligible clients admitted to the test. */
  readonly trafficAllocation: number;
  /** Do not make a scale decision before both arms reach this denominator. */
  readonly minExposuresPerArm: number;
  /** Mandatory review point; this policy never auto-scales an experiment. */
  readonly reviewAfterDays: number;
  readonly primaryMetric: string;
}

export interface ExperimentAssignmentPolicy {
  readonly assignmentVersion: string;
  readonly analysisStartDate: string;
  readonly acceptUnversionedFrom: string;
  readonly acceptUnversionedThrough: string;
  readonly invalidBeforeReason: string;
}

export interface ExperimentConfig {
  readonly id: string;
  readonly status: "active" | "off";
  /** IST calendar date the experiment is meant to start; the readout's anchor. */
  readonly startDate: string;
  /** Immutable assignment/readout epoch. Shared with the lake readout via JSON. */
  readonly assignmentPolicy: ExperimentAssignmentPolicy;
  readonly arms: readonly ExperimentArm[];
  readonly description: string;
  /** Optional bounded-pilot policy. Omitted experiments admit all eligible clients. */
  readonly pilot?: ExperimentPilotPolicy;
  /**
   * WHERE to call `trackExperimentExposure(id)` — the single decision point at
   * which the arms' behavior actually diverges (NOT on every page view).
   * Exposure discipline: emit only where a seeker could observe a difference,
   * otherwise the readout dilutes with never-treated users.
   */
  readonly exposurePoint: string;
}

const ASSIGNMENT_POLICIES = EXPERIMENT_ASSIGNMENT_POLICIES as Readonly<
  Record<string, ExperimentAssignmentPolicy>
>;

export function experimentAssignmentPolicy(id: string): ExperimentAssignmentPolicy {
  const policy = ASSIGNMENT_POLICIES[id];
  if (!policy) throw new Error(`Experiment ${id} is missing assignment policy`);
  return policy;
}

export const EXPERIMENTS: readonly ExperimentConfig[] = [
  {
    id: "kundali_preview_pilot_2026_09",
    status: "active",
    startDate: "2026-09-17",
    assignmentPolicy: experimentAssignmentPolicy("kundali_preview_pilot_2026_09"),
    arms: [
      { id: "control", weight: 1 },
      { id: "preview", weight: 1 },
    ],
    pilot: {
      trafficAllocation: 0.2,
      minExposuresPerArm: 100,
      reviewAfterDays: 28,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Show calculated chart value before mobile capture on AI Astrologer and English/Hindi Kundali. Capture still precedes saving and chat continuation. Compare activation, contact completion, revenue and generation cost before any expansion.",
    exposurePoint:
      "First form interaction, before contact fields diverge; exclude returning contacts, progressive and paid-report entries.",
  },
  {
    // RETIRED by ADR-0043. Kept for historical exposure/readout compatibility;
    // optional and post-value phone capture are no longer runtime policies.
    id: "phone_gate_2026_07",
    status: "off",
    startDate: "2026-07-02",
    assignmentPolicy: experimentAssignmentPolicy("phone_gate_2026_07"),
    arms: [
      { id: "required", weight: 1 }, // control: phone required on the chat-entry kundali form (today's behavior, 68adf5ba60)
      { id: "optional_nudge", weight: 1 }, // phone optional + WhatsApp-delivery nudge copy on the same field
      { id: "post_value", weight: 1 }, // no contact fields at entry; the ask moves AFTER the reading renders (chat post-value card + in-chat captures)
    ],
    description:
      "Historical required vs optional/post-value phone-capture pilot. ADR-0043 accepts the " +
      "measured activation trade-off and locks mandatory phone identity on the shared form.",
    exposurePoint: "Retired — no new exposures after ADR-0043.",
  },
  {
    // RETIRED full-traffic WS-K resolution_7d experiment (Engine 3, 2026-07-02).
    // Kept immutable so historical rows retain their original id and arms. The
    // bounded replacement below is the only active resolution experiment. The
    // runtime flag NEXT_PUBLIC_RESOLUTION_PACKAGE_ENABLED remains the product
    // kill switch; this entry must stay off.
    id: "resolution_package_v1",
    status: "off",
    startDate: "2026-07-02",
    assignmentPolicy: experimentAssignmentPolicy("resolution_package_v1"),
    arms: [
      { id: "control", weight: 1 }, // today's paywall + ladder (bare ₹149 report next step)
      { id: "package", weight: 1 }, // the ₹199 package: session_expired primary + ladder target swap
    ],
    description:
      "Does the resolution_7d package (chart-matched remedy report + 7-day un-gated chat) " +
      "lift chat ₹-per-exposed-reading vs today's ₹29/₹149 ladder? Governing metric = net " +
      "revenue per exposed reading (never per-SKU counts — report cannibalization is expected " +
      "by construction). Kill = flip the flag off. Note: the design's reading_id unit is the " +
      "intent; this WS-F rail assigns by browser distinct_id — acceptable (a reading is one " +
      "browser session in practice), and the private order-id join is unit-agnostic.",
    exposurePoint:
      "The chat paywall (ChatPaywall) when the seeker is ELIGIBLE for the package (flag on + a " +
      "resolvable allowlisted dosha in the payload) — call trackExperimentExposure('resolution_" +
      "package_v1') ONCE per session there, for BOTH arms, since that is where the treatment " +
      "arm's session_expired primary + ladder-target-swap diverge from control. Do NOT emit from " +
      "the offer card render (only the treatment arm mounts it → no control denominator).",
  },
  {
    // The original full-traffic experiment above is retained as immutable
    // history. This new id creates a clean causal boundary for the deliberately
    // bounded customer pilot requested on 2026-07-10.
    id: "resolution_package_pilot_2026_07",
    status: "active",
    startDate: "2026-07-10",
    assignmentPolicy: experimentAssignmentPolicy("resolution_package_pilot_2026_07"),
    arms: [
      { id: "control", weight: 1 },
      { id: "package", weight: 1 },
    ],
    pilot: {
      trafficAllocation: 0.2,
      minExposuresPerArm: 100,
      reviewAfterDays: 28,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Bounded validation of resolution_7d before any scale decision. Admit 20% of eligible " +
      "seekers, split them evenly between today's ladder and the package, and review net revenue " +
      "per exposed reading only after the governed denominator and duration are met. This policy " +
      "never auto-scales.",
    exposurePoint:
      "The chat paywall when the seeker is eligible for resolution_7d. Emit once for admitted " +
      "control and package clients; clients outside the allocation stay on today's experience " +
      "and are not stamped into the experiment readout.",
  },
  {
    // RETIRED 2026-07-13. Product made post-answer timing universal before the
    // bounded pilot reached its governed readout. Keep the id, arms, assignment
    // epoch and pilot policy immutable for the small historical exposure set;
    // status off ensures no new assignment, exposure, or payment-note stamping.
    id: "chat_offer_timing_pilot_2026_07",
    status: "off",
    startDate: "2026-07-12",
    assignmentPolicy: experimentAssignmentPolicy("chat_offer_timing_pilot_2026_07"),
    arms: [
      { id: "control", weight: 1 },
      { id: "answer_timed", weight: 1 },
    ],
    pilot: {
      trafficAllocation: 0.2,
      minExposuresPerArm: 100,
      reviewAfterDays: 14,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Retired mobile-chat timing pilot. The report card and sticky re-entry now universally defer " +
      "until the first non-empty Jyotish answer, so no live control/treatment divergence remains. " +
      "Historical rows retain these immutable arms; do not use the incomplete pilot as a causal readout.",
    exposurePoint:
      "Historical only: mobile /chat when a pre-answer chart-ready report offer first became " +
      "eligible. The runtime no longer assigns or emits this exposure because timing no longer diverges.",
  },
  {
    id: "offer_proof_first_pilot_2026_07",
    status: "active",
    startDate: "2026-07-13",
    assignmentPolicy: experimentAssignmentPolicy("offer_proof_first_pilot_2026_07"),
    arms: [
      { id: "control", weight: 1 },
      { id: "proof_first", weight: 1 },
    ],
    pilot: {
      trafficAllocation: 0.2,
      minExposuresPerArm: 100,
      reviewAfterDays: 14,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Does moving one compact engine/catalog-backed proof line above price improve the report " +
      "offer's CTA-seen to CTA-tapped step and net revenue without adding another decision? " +
      "Only cards with chart proof or governed value proof are eligible.",
    exposurePoint:
      "PaidReportOfferCard after the eligible card is at least 50% visible for one continuous " +
      "second. Both admitted control and proof_first arms emit there; never expose on mount, " +
      "below-fold non-views, or cards with no proof-backed divergence.",
  },
  {
    id: "session_price_pilot_2026_07",
    status: "active",
    startDate: "2026-07-24",
    assignmentPolicy: experimentAssignmentPolicy("session_price_pilot_2026_07"),
    arms: [
      { id: "control", weight: 1 }, // quick stays at today's ₹29
      { id: "p99", weight: 1 }, // the SAME ranked quick slot sells quick_p99 at ₹99
    ],
    pilot: {
      trafficAllocation: 1.0,
      minExposuresPerArm: 500,
      reviewAfterDays: 14,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Does repricing the 30-min quick session ₹29→₹99 lift net revenue per exposed client " +
      "without collapsing chat continuation? Context: 80 units/30d at ₹29 ≈ ₹2.3K — a 3.4× " +
      "price needs only ~30% of today's volume to break even. Governing metric = net revenue " +
      "per exposed client, never per-SKU counts (a continuation-volume drop is expected by " +
      "construction). The swap is presentation-side on the SAME ranked quick slot " +
      "(session-price-experiment.ts); the server charges from the quick_p99 catalog row, and " +
      "the rail fails open to ₹29 control pricing until that row is live.",
    exposurePoint:
      "The chat paywall (ChatPaywall) when a quick-family session offer is presented in the " +
      "ranked list AND the quick_p99 catalog row is live — call trackExperimentExposure(" +
      "'session_price_pilot_2026_07') ONCE per session there, for BOTH arms (control needs a " +
      "denominator). Before the catalog row deploys the arms cannot diverge, so no exposure " +
      "is emitted.",
  },
  {
    id: "chat_concern_match_pilot_2026_07",
    status: "active",
    startDate: "2026-07-16",
    assignmentPolicy: experimentAssignmentPolicy("chat_concern_match_pilot_2026_07"),
    arms: [
      { id: "control", weight: 1 }, // today's chain: deep-link → entry intent → dosha → default
      { id: "concern_match", weight: 1 }, // conversation concern ranks above entry intent, below deep-link
    ],
    pilot: {
      trafficAllocation: 0.2,
      minExposuresPerArm: 100,
      reviewAfterDays: 14,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Does ranking the chat reading card's report by the conversation's EXPRESSED concern " +
      "(above entry intent, below an explicit deep-link) lift net revenue per exposed client? " +
      "Context: chat-reading-primary CTR 0.39% vs 3.26% on kundali-result-primary, while " +
      "concern/signal-matched placements massively outperform generic ones (owned events, 14d). " +
      "The concern→plan mapping REUSES intentFromChatText + the rule-40 governed intent→offer " +
      "policy (concernMatchedReportPlanId) — no parallel map to drift.",
    exposurePoint:
      "The chat reading offer card (ChatReadingReportOffer) inside its fire-once visible " +
      "impression, ONLY when the concern-derived plan DIFFERS from the control-arm plan for " +
      "this reading. Both admitted arms emit there — no divergence, no exposure, so the " +
      "readout is not diluted with never-treated users.",
  },
  {
    // RETIRED 2026-07-22 after crossing the governed 100-client/arm floor.
    // Lake readout: 178 control and 172 upgrade clients, zero Complete Reading
    // orders, and offer taps of 3 control vs 0 upgrade. Preserve id/arms/epoch
    // for history; status off stops new exposure and payment attribution.
    id: "report_bundle_upgrade_pilot_2026_07",
    status: "off",
    startDate: "2026-07-17",
    assignmentPolicy: experimentAssignmentPolicy("report_bundle_upgrade_pilot_2026_07"),
    arms: [
      { id: "control", weight: 1 }, // today's card: the plan checkout only, no toggle
      { id: "upgrade", weight: 1 }, // one compact Complete Reading toggle inside the SAME card
    ],
    pilot: {
      trafficAllocation: 0.2,
      minExposuresPerArm: 100,
      reviewAfterDays: 14,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Retired Complete Reading checkbox pilot. Its 2026-07-22 lake readout crossed the " +
      "governed denominator (178 control and 172 upgrade clients) with zero Complete Reading " +
      "orders; offer taps were 3 control vs 0 upgrade. The result rejects the acquisition-card " +
      "checkbox, not the server-authoritative ₹399 bundle contract. Historical rows retain the " +
      "immutable id, arms, and assignment epoch for reproducibility.",
    exposurePoint:
      "Historical only: PaidReportOfferCard when the Brihat primary pick and Complete Reading " +
      "bundle summary were both available. The runtime no longer assigns or emits this exposure.",
  },
  {
    // RETIRED 2026-09-08 without promotion. The pilot's 14-day review fell
    // due on 2026-08-02, two weeks after Google's 2026-07-21 site-level
    // demotion cut the Tithi traffic it was measuring, so its readout is
    // confounded; and its arm was decided in the browser AFTER hydration
    // (the server always rendered control), so treatment visitors saw the
    // page change under them and the server-versus-hydrated certification
    // rejected /tithi-today/. Preserve id/arms/epoch for history; status off
    // keeps every visitor on the current UI until a server-assigned rerun.
    id: "daily_timing_v2_pilot_2026_07",
    status: "off",
    startDate: "2026-07-19",
    assignmentPolicy: experimentAssignmentPolicy("daily_timing_v2_pilot_2026_07"),
    arms: [
      { id: "control", weight: 1 },
      { id: "daily_timing_v2", weight: 1 },
    ],
    pilot: {
      trafficAllocation: 1.0,
      minExposuresPerArm: 500,
      reviewAfterDays: 14,
      primaryMetric: "net_revenue_per_exposed_client",
    },
    description:
      "Retired Daily Timing v2 pilot (no promotion; see the retirement note above). It asked: " +
      "does the server-prefetched, fully localized, one-purpose Daily Timing v2 journey lift " +
      "net revenue per exposed Tithi visitor versus today's Tithi result while preserving one " +
      "engine-backed answer and one existing Muhurat CTA? The treatment cannot promote until " +
      "the readout also clears its 2.5% offer-tap and refund guardrails.",
    exposurePoint:
      "Historical only: TithiTodaySurface after hydration, immediately before the assigned current or v2 " +
      "journey becomes observable. Emit once for BOTH arms; the deterministic browser rail " +
      "keeps current UI for control and renders v2 only for daily_timing_v2.",
  },
];

export function experimentConfig(
  id: string,
  registry: readonly ExperimentConfig[] = EXPERIMENTS
): ExperimentConfig | undefined {
  return registry.find((e) => e.id === id);
}

export function activeExperiments(
  registry: readonly ExperimentConfig[] = EXPERIMENTS
): readonly ExperimentConfig[] {
  return registry.filter((e) => e.status === "active");
}
